import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n } from 'react-redux-i18n-lite';
import * as classroomActions from '../../actions/classroom/classroomActions';
import * as classroomGroupActions from '../../actions/classroom/classroomGroupActions';
import * as drawerActions from '../../actions/drawerActions';
import * as bookActions from '../../actions/bookActions';
import { GROUPS, ROLE } from '../../const';
import { DrawerCommonPanelCustom } from '../classroom/students/AddUsers/AddUserComponents';
import GroupsCarousel from '../book/right-panel/GroupsCarousel';
import MultipleSelectionPanel from './MultipleSelectionPanel';
import CustomIcon from '../common/CustomIcon';

const InfoContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 0 0 16px 0;
  width: 100%;
  box-sizing: border-box;

  i {
    color: #666;
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    font-size: 14px;
    color: #666;
    margin: 0;
    line-height: 1.4;
  }
`;

const StudentSelectButton = ({ icon, hasSelection, onClick }) => {
  const colorStyle = hasSelection ? '' : 'grayscale';
  return (
    <div className="students-group-component" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div className={`students-group-circle choose ${colorStyle}`}>
        <img src={icon} alt="presentation" className={colorStyle} />
      </div>
      <div className={`students-group-title choose primary ${colorStyle}`}>
        {I18n.t('rightPanel.individual.selectStudent')}
      </div>
    </div>
  );
};

StudentSelectButton.propTypes = {
  icon: PropTypes.string,
  hasSelection: PropTypes.bool,
  onClick: PropTypes.func
};

export class NeaeAdaptationsPanel extends React.Component {
  state = {
    chooseStudent: false,
    currentSelection: {}
  };

  componentDidMount() {
    const { classroom, classroomActions, classroomGroupActions } = this.props;

    classroomGroupActions.getClassroomGroups(classroom.id);
    classroomActions.getClassroomParticipants(classroom.id);
  }

  closePanel = () => this.props.drawerActions.closeDrawerAndRemoveContent();

  getAvailableGroups = () => {
    const { groups } = this.props;
    return (groups || []).filter(group => group.id !== GROUPS.EVERYONE.id);
  };

  selectGroup = groupId => {
    this.setState({ currentSelection: { id: groupId, isGroup: true } });
  };

  selectStudent = student => {
    const { currentSelection } = this.state;
    const currentUsers = currentSelection.users || [];
    const filteredUsers = currentUsers.filter(user => user._id !== student._id);
    const isRemoving = filteredUsers.length !== currentUsers.length;

    this.setState({
      currentSelection: {
        isGroup: false,
        users: isRemoving ? filteredUsers : [...currentUsers, student]
      }
    });
  };

  openChooseStudent = () => this.setState({ chooseStudent: true });

  backToGroups = () => this.setState({ chooseStudent: false });

  hasValidSelection = () => {
    const { currentSelection } = this.state;

    if (currentSelection.isGroup) {
      return currentSelection.id !== undefined && currentSelection.id !== GROUPS.EVERYONE.id;
    }

    return Boolean(currentSelection.users && currentSelection.users.length >= 1);
  };

  hasSelectedStudent = () => {
    const { currentSelection } = this.state;
    return Boolean(!currentSelection.isGroup && currentSelection.users && currentSelection.users.length);
  };

  adapt = () => {
    if (!this.hasValidSelection()) {
      return;
    }

    this.launchAdaptation();
  };

  launchAdaptation = () => {
    const { bookActions, book, classroom, selectedResource } = this.props;
    const { currentSelection } = this.state;

    bookActions.createNeaeAdaptation(
      selectedResource,
      classroom.id,
      book.id,
      selectedResource.unitActivityId,
      currentSelection
    );

    this.closePanel();
  };

  renderInfo = () => (
    <InfoContainer>
      <CustomIcon name="information-circle" size={18} />
      <p>{I18n.t('rightPanel.neaeAdaptations.info')}</p>
    </InfoContainer>
  );

  renderGroupSelection = () => {
    const { individualIcon } = this.props;
    const { currentSelection } = this.state;
    const availableGroups = this.getAvailableGroups();

    return (
      <DrawerCommonPanelCustom
        closePanel={this.closePanel}
        panelHeaderTitle={I18n.t('rightPanel.neaeAdaptations.panelTitle')}
        hideRowButtons={!this.hasValidSelection()}
        primaryButtonText={'rightPanel.neaeAdaptations.adaptButton'}
        onPrimaryButtonClick={this.adapt}
        footerTopContent={this.hasValidSelection() ? this.renderInfo() : null}
      >
        <span className="section-title-label">{I18n.t('rightPanel.groups.selectGroup')}</span>
        <div className="students-groups-container panel">
          <GroupsCarousel
            currentSelection={currentSelection}
            hasMultipleChoose={true}
            groups={availableGroups}
            selectGroup={this.selectGroup}
          />
          <div className="choose-students-container">
            {availableGroups.length ? (
              <div className="group-circles-separator"> {I18n.t('common.or')} </div>
            ) : (
              ''
            )}
            <StudentSelectButton
              icon={individualIcon}
              hasSelection={this.hasSelectedStudent()}
              onClick={this.openChooseStudent}
            />
          </div>
        </div>
      </DrawerCommonPanelCustom>
    );
  };

  renderStudentSelection = () => {
    const { students } = this.props;
    const { currentSelection } = this.state;
    const hasSelection = this.hasValidSelection();

    return (
      <DrawerCommonPanelCustom
        closePanel={this.closePanel}
        panelHeaderTitle={I18n.t('rightPanel.neaeAdaptations.panelTitle')}
        hideRowButtons={false}
        hidePrimaryButton={!hasSelection}
        showCancelButton={true}
        handleSecondaryButtonClick={this.backToGroups}
        secondaryButtonText={'common.back'}
        icon={'left-chevron'}
        primaryButtonText={'rightPanel.neaeAdaptations.adaptButton'}
        onPrimaryButtonClick={this.adapt}
        footerTopContent={hasSelection ? this.renderInfo() : null}
      >
        <MultipleSelectionPanel
          title={'rightPanel.individual.assignTitle'}
          students={students}
          selectStudent={this.selectStudent}
          currentSelection={currentSelection.users || []}
        />
      </DrawerCommonPanelCustom>
    );
  };

  render() {
    const { chooseStudent } = this.state;
    return chooseStudent ? this.renderStudentSelection() : this.renderGroupSelection();
  }
}

NeaeAdaptationsPanel.propTypes = {
  classroom: PropTypes.any,
  classroomActions: PropTypes.any,
  classroomGroupActions: PropTypes.any,
  drawerActions: PropTypes.any,
  bookActions: PropTypes.any,
  students: PropTypes.any,
  groups: PropTypes.any,
  individualIcon: PropTypes.string,
  selectedResource: PropTypes.any,
  book: PropTypes.any
};

function mapStateToProps(state) {
  return {
    groups: state.classrooms.selectedClassroom.groups,
    classroom: state.classrooms.selectedClassroom.classroom,
    students: state.classrooms.selectedClassroom.participants.filter(participant => participant.role === ROLE.STUDENT),
    individualIcon: state.theme.images.icons.individualColor,
    selectedResource: state.book.selectedResource,
    book: state.book.book
  };
}

function mapDispatchToProps(dispatch) {
  return {
    classroomActions: bindActionCreators(classroomActions, dispatch),
    classroomGroupActions: bindActionCreators(classroomGroupActions, dispatch),
    drawerActions: bindActionCreators(drawerActions, dispatch),
    bookActions: bindActionCreators(bookActions, dispatch),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NeaeAdaptationsPanel);
